import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Mail, ShieldCheck } from "lucide-react";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { env } from "@/lib/env";
import { getOrCreateDeviceToken } from "@/lib/deviceId";
import { MOCK_OTP_CODE } from "@/mocks/fixtures";
import { codePasswordForm, type CodePasswordForm } from "../schemas";
import { useRegisterStart, useRegisterComplete } from "../api/hooks";
import { useAuthStore } from "../store";
import { IconInput } from "./IconInput";
import { PasswordInput } from "./PasswordInput";
import { AuthButton } from "./AuthButton";
import { AuthError } from "./AuthError";

/** New-device verification. The password login was rejected because this browser isn't a
 *  trusted device yet, so we send a 6-digit code to the on-file email and let the user
 *  confirm (register/complete) — which enrolls the device on success. `onBack` → login. */
export function ReverifyFlow({ email, onBack }: { email: string; onBack: () => void }) {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [limitReached, setLimitReached] = useState(false);

  const start = useRegisterStart();
  const complete = useRegisterComplete();

  // Send the code once when the screen mounts (the login attempt already told us who this is).
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    start.mutate({ email }, { onSuccess: ({ sentTo }) => setSentTo(sentTo) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = useForm<CodePasswordForm>({
    resolver: zodResolver(codePasswordForm),
    defaultValues: { code: "", password: "" },
  });

  const onSubmit = form.handleSubmit(({ code, password }) => {
    complete.mutate(
      { email, code, password, deviceToken: getOrCreateDeviceToken() },
      {
        onSuccess: ({ token, user, status, deviceLimitReached }) => {
          setSession(token, user, status);
          if (deviceLimitReached) setLimitReached(true);
          else navigate("/dashboard", { replace: true });
        },
      }
    );
  });

  if (limitReached) {
    return (
      <div className="space-y-6 text-center">
        <div className="mx-auto flex size-11 items-center justify-center rounded-full bg-teal-400/20">
          <CheckCircle className="size-6 text-teal-400" />
        </div>
        <div className="space-y-1">
          <h2 className="text-xl font-semibold">Du er logget inn</h2>
          <p className="text-sm text-white/80">
            Du har alt 3 betrodde enheter. Fjern én under «Dine enheter» for å betro denne.
          </p>
        </div>
        <AuthButton type="button" onClick={() => navigate("/dashboard", { replace: true })}>
          Fortsett
        </AuthButton>
      </div>
    );
  }

  return (
    <Form key="reverify" {...form}>
      <form onSubmit={onSubmit} className="space-y-5" noValidate>
        <div className="space-y-2 text-center">
          <h2 className="text-xl font-semibold">Bekreft ny enhet</h2>
          <p className="text-sm text-white/80">
            Vi sendte en kode til{" "}
            <span className="font-semibold text-white">{sentTo ?? email}</span>. Oppgi den og
            passordet ditt.
          </p>
        </div>

        {env.useMocks && (
          <p className="text-center text-sm text-white/70">
            Demo-modus — bruk kode{" "}
            <span className="font-mono font-medium text-white">{MOCK_OTP_CODE}</span>.
          </p>
        )}

        {complete.isError && <AuthError message={complete.error.message} />}

        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <IconInput
                  icon={<ShieldCheck className="h-5 w-5" />}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6-sifret kode"
                  autoComplete="one-time-code"
                  autoFocus
                  name={field.name}
                  ref={field.ref}
                  onBlur={field.onBlur}
                  value={field.value ?? ""}
                  // Digits only — strip whitespace/letters, cap at 6.
                  onChange={(e) => field.onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
                />
              </FormControl>
              <FormMessage className="ml-4 text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <PasswordInput
                  placeholder="Passord"
                  autoComplete="current-password"
                  name={field.name}
                  ref={field.ref}
                  onBlur={field.onBlur}
                  value={field.value ?? ""}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage className="ml-4 text-red-400" />
            </FormItem>
          )}
        />

        {sentTo === null && start.isPending && (
          <p className="flex items-center justify-center gap-2 text-sm text-white/60">
            <Mail className="size-4 text-teal-300" /> Sender kode…
          </p>
        )}

        <div className="flex flex-col gap-3 sm:flex-row">
          <AuthButton type="button" variant="outline" onClick={onBack}>
            Avbryt
          </AuthButton>
          <AuthButton type="submit" loading={complete.isPending}>
            Logg inn
          </AuthButton>
        </div>
      </form>
    </Form>
  );
}
